import { animate, style, transition, trigger } from "@angular/animations";
import { HttpErrorResponse } from "@angular/common/http";
import { Component } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { interval, Subject, timer } from "rxjs";
import { finalize, startWith, switchMap, takeUntil } from "rxjs/operators";
import { ConfirmationComponent } from "../../../../../src/app/shared/dialogs/confirmation/confirmation.component";
import { ShowQrComponent } from "../../../../../src/app/shared/dialogs/show-qr/show-qr.component";
import { DataService } from "../../../../../src/app/shared/services/data.service";
import { LoaderService } from "../../../../../src/app/shared/services/loader/loader.service";
import {
  Leaderboard,
  ResponseData,
  TeamGroup,
} from "../../../../../src/app/shared/services/teacher.interface";

@Component({
  selector: "app-leaderboard",
  templateUrl: "./leaderboard.component.html",
  styleUrls: ["./leaderboard.component.scss"],
  animations: [
    trigger("slideUpAnimation", [
      transition(":enter", [
        style({ transform: "translateY(100%)", opacity: 0 }),
        animate(
          "500ms ease-out",
          style({ transform: "translateY(0)", opacity: 1 })
        ),
      ]),
      transition(":leave", [
        animate(
          "10ms ease-in",
          style({ transform: "translateY(-100%)", opacity: 0 })
        ),
      ]),
    ]),
  ],
})
export class LeaderboardComponent {
  quizStarted: boolean = false;
  numbers = [10, 9, 8, 7, 6, 5, 4, 3, 2, 1];
  activeNumber: number | null = null;
  private currentIndex = 0;

  canShowTimer = false;
  sessionData = sessionStorage.getItem("GCTeamId");
  groupId = this.sessionData ? this.sessionData : '';
  timeRemaining: string = "10:00";
  private durationInSeconds: number = 600; 
  private stop$ = new Subject<void>();
  isTimeUp: boolean = false; 
  disablePopUp = false;
  leaderboard: Leaderboard;

  constructor(
    private dataService: DataService,
    private router: Router,
    private dialog: MatDialog,
    private loader: LoaderService
  ) {}

  ngOnInit(): void {
    this.getTeamGroup();
  }

  getTeamGroup() {
    this.loader.display(true);
    this.dataService.getTeamGroup(this.groupId).subscribe({
      next: (res: ResponseData<TeamGroup>) => {
        if (res.data.status == 103) {
          this.stop$.next();
          this.router.navigateByUrl("/quiz-completed");
        } else {
          if (this.getRemainigTime(res.data.end_time) > 600) {
            this.startCountdown();
          }
          this.canShowTimer = true;
          sessionStorage.setItem("end_time", res.data.end_time.toString());
          this.initializeTimer();
          this.startPolling();
        }
        this.loader.display(false);
      },
      error: (err: HttpErrorResponse) => {
        console.error(err);
        this.loader.display(false);
      },
    });
  }

  getRemainigTime(endTime: number): number {
    const currentTimeUnix = Math.floor(Date.now() / 1000);
    return Math.max(0, endTime - currentTimeUnix);
  }

  startCountdown() {
    this.quizStarted = true;
    this.showNumber(this.currentIndex);
    const interval = setInterval(() => {
      this.currentIndex++;
      if (this.currentIndex < this.numbers.length) {
        this.activeNumber = null;
        setTimeout(() => {
          this.showNumber(this.currentIndex);
        }, 100);
      } else {
        clearInterval(interval);
        this.quizStarted = false;
      }
    }, 1000);
  }

  showNumber(index: number) {
    this.activeNumber = this.numbers[index];
  }

  // Initialize and start the countdown timer
  initializeTimer() {
    const endTime = Number(sessionStorage.getItem("end_time"));
    const currentTimeUnix = Math.floor(Date.now() / 1000); // Get current Unix time (seconds since epoch)
    this.durationInSeconds = Math.max(0, endTime - currentTimeUnix); // Calculate remaining time, ensuring it doesn't go below 0

    timer(0, 1000)
      .pipe(
        startWith(0),
        takeUntil(this.stop$),
        finalize(() => {
          this.isTimeUp = true;
        })
      )
      .subscribe(() => {
        if (this.durationInSeconds > 600) {
          // If more than 10 minutes, fix the time at 10:00
          // because currently we get the difference in time 10:05
          this.durationInSeconds--;
          this.timeRemaining = this.formatTime(600);
        } else if (this.durationInSeconds > 0) {
          // Countdown for 10 minutes or less
          this.durationInSeconds--;
          this.timeRemaining = this.formatTime(this.durationInSeconds);
        } else {
          // Stop the timer when time is up
          this.stopPolling();
        }
      });
  }

  // Format time in mm:ss
  formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${this.padZero(minutes)}:${this.padZero(remainingSeconds)}`;
  }

  // Helper to pad numbers with zero
  padZero(num: number): string {
    return num < 10 ? `0${num}` : `${num}`;
  }

  // Start API polling every 5 seconds
  startPolling() {
    interval(5000)
      .pipe(
        startWith(0), // Call API immediately on start
        takeUntil(this.stop$), // Stop polling when the component is destroyed
        switchMap(() => this.dataService.getLeaderboard(this.groupId))
      )
      .subscribe({
        next: (res: ResponseData<Leaderboard>) => {
          if (res.data.status == 103) {
            this.stop$.next();
            this.disablePopUp = true;
            setTimeout(() => {
              //2.5 seconds delay for showing updated progress before leaving
              this.router.navigateByUrl("/quiz-completed");
            }, 2500);
          }
          this.leaderboard = res.data;
          sessionStorage.setItem("end_time", res.data.end_time.toString());
        },
        error: (err) => {
          console.error(err);
        },
      });
  }

  // Stop polling and timers
  stopPolling() {
    this.stop$.next(); // Trigger stop signal
    this.disablePopUp = true;
    this.changeQuizStatus();
    setTimeout(() => {
      this.router.navigateByUrl("/quiz-completed");
    }, 2500);
  }

  showQrCode() {
    this.dialog.open(ShowQrComponent, {
      disableClose: true,
    });
  }

  changeQuizStatus() {
    let params = {
      status: 103,
    };
    this.dataService.startQuiz(this.groupId, params).subscribe({
      next: (res: ResponseData<TeamGroup>) => {
        console.log(res.data);
      },
      error: (err: HttpErrorResponse) => {
        console.log(err);
      },
    });
  }

  endQuiz() {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      disableClose: true,
      data: { isTeacher: true },
    });
    dialogRef.afterClosed().subscribe((data: boolean) => {
      if (data) {
        let params = {
          status: 103,
        };
        this.dataService.startQuiz(this.groupId, params).subscribe({
          next: (res: ResponseData<TeamGroup>) => {
            if (res.data) {
              this.stopPolling();
              this.router.navigateByUrl("/quiz-completed");
            }
          },
          error: (err: HttpErrorResponse) => {
            this.stopPolling();
            console.log(err);
          },
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.stop$.next(); // Clean up when the component is destroyed
    sessionStorage.removeItem("end_time");
    this.dialog.closeAll();
  }
}
